import type {
  LocalizedStringValue,
  ProductDocument,
  ProductManualDocument,
  ProductVariant,
  ProductTypeCategorySiteVariant,
  ProductTypeCategoryDocument,
  SiteAwareProductTypeCategoryDocument,
} from '@/types/sanity'
import type {SiteLite} from '@/multisite/types'
import {localizedProductTypeLabel} from '@/lib/productTypeRouting'
import {isCampaignWindowActive} from '@/lib/campaignTimeZones'

export const localizedFieldKey = (locale?: string): keyof LocalizedStringValue => {
  switch (locale) {
    case 'nb-NO':
      return 'nbNO'
    case 'sv-SE':
      return 'svSE'
    case 'fr-FR':
      return 'frFR'
    case 'fr-CA':
      return 'frCA'
    case 'de-DE':
      return 'deDE'
    case 'es-ES':
      return 'esES'
    case 'it-IT':
      return 'itIT'
    case 'fi-FI':
      return 'fiFI'
    case 'nl-NL':
      return 'nlNL'
    case 'pl-PL':
      return 'plPL'
    case 'da-DK':
      return 'daDK'
    case 'cs-CZ':
      return 'csCZ'
    case 'en-US':
      return 'enUS'
    case 'en-CA':
      return 'enCA'
    default:
      return 'enGB'
  }
}

export const localizedValue = (
  value: LocalizedStringValue | undefined,
  locale?: string,
  fallback: Array<keyof LocalizedStringValue> = ['enGB', 'enUS', 'nbNO', 'svSE'],
) => {
  if (!value) return undefined

  const primaryKey = localizedFieldKey(locale)
  if (value[primaryKey]) return value[primaryKey]

  for (const key of fallback) {
    if (value[key]) return value[key]
  }

  return undefined
}

export const productTitle = (product: ProductDocument) =>
  product.title || product.productNumber || 'Product'

export const productSeries = (product: ProductDocument) =>
  product.brand?.title || product.brandKey || 'Serie'

export const productTypeLabel = (
  productType:
    | ProductTypeCategoryDocument
    | SiteAwareProductTypeCategoryDocument
    | null
    | undefined,
  locale?: string,
) =>
  (productType ? localizedProductTypeLabel(productType, locale) : undefined) ||
  productType?.title ||
  productType?.key

export const productSummarySpecs = (product: ProductDocument, locale?: string, limit = 4) =>
  (product.technicalSpecifications || []).slice(0, limit).map((spec) => ({
    key: spec._key || spec.code || `${product._id}-spec-${spec.value || 'value'}`,
    label: localizedValue(spec.label, locale) || spec.code || 'Specification',
    value: spec.value || '-',
  }))

export const productHighlights = (product: ProductDocument, locale?: string, limit = 3) =>
  (product.usps || [])
    .map((usp) => ({
      key:
        usp._key ||
        usp.code ||
        `${product._id}-usp-${usp.label?.enGB || usp.label?.enUS || 'item'}`,
      label: localizedValue(usp.label, locale) || usp.code || '',
    }))
    .filter((highlight) => highlight.label.trim().length > 0)
    .slice(0, limit)

export const productDescription = (product: ProductDocument, locale?: string) =>
  localizedValue(product.descriptionI18n, locale, ['enGB', 'enUS', 'nbNO', 'svSE'])

// Keep these original asset URLs untransformed: productCardImageFit reads their
// intrinsic dimensions. Adding width/crop/fit transformations here would silently
// disable the near-square card fill and fall back to object-contain.
export const productHeroImageUrl = (product: ProductDocument) =>
  product.heroImage?.url ||
  product.variants?.find((variant) => variant.images?.[0]?.url)?.images?.[0]?.url

export const productDocumentTitle = (
  document: ProductManualDocument,
  locale?: string,
  fallback = 'Document',
) => (locale && document.displayNames?.[locale]) || document.filename || document.title || fallback

const localeForStringCompare = (locale?: string) => {
  if (!locale) return undefined

  try {
    Intl.getCanonicalLocales(locale)
    return locale
  } catch {
    return undefined
  }
}

export const sortProductDocuments = (documents: ProductManualDocument[], locale?: string) =>
  [...documents].sort((left, right) => {
    const leftSortOrder =
      typeof left.sortOrder === 'number' ? left.sortOrder : Number.MAX_SAFE_INTEGER
    const rightSortOrder =
      typeof right.sortOrder === 'number' ? right.sortOrder : Number.MAX_SAFE_INTEGER
    if (leftSortOrder !== rightSortOrder) return leftSortOrder - rightSortOrder

    return productDocumentTitle(left, locale).localeCompare(
      productDocumentTitle(right, locale),
      localeForStringCompare(locale),
      {
        numeric: true,
        sensitivity: 'base',
      },
    )
  })

const normalizeLocaleKey = (value?: string | null) =>
  String(value || '')
    .trim()
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase()

export const productDocumentMatchesLocale = (
  document: ProductManualDocument,
  locale?: string,
): boolean => {
  const localeKeys = document.localeKeys || []
  if (!localeKeys.length) return true

  const normalizedLocale = normalizeLocaleKey(locale)
  if (!normalizedLocale) return true

  return localeKeys.some((key) => normalizeLocaleKey(key) === normalizedLocale)
}

export const filterProductDocumentsByLocale = (
  documents: ProductManualDocument[],
  locale?: string,
): ProductManualDocument[] =>
  documents.filter((document) => productDocumentMatchesLocale(document, locale))

const normalizeMarketKey = (marketKey?: string) =>
  String(marketKey || '')
    .trim()
    .toUpperCase()

/**
 * `now` is injectable so the campaign window can be tested at a fixed instant
 * rather than only at whatever time the suite happens to run.
 */
export const productVariantPriceForMarket = (
  variant: ProductVariant,
  marketKey?: string,
  now: Date = new Date(),
) => {
  const normalizedMarketKey = normalizeMarketKey(marketKey)
  const marketPrice = normalizedMarketKey
    ? variant.prices?.[normalizedMarketKey]
    : Object.values(variant.prices || {})[0]
  if (typeof marketPrice?.amount === 'number') {
    const campaignAmount =
      typeof marketPrice.campaignAmount === 'number' &&
      marketPrice.campaignAmount < marketPrice.amount &&
      isCampaignWindowActive(marketPrice, normalizedMarketKey, now)
        ? marketPrice.campaignAmount
        : undefined
    return {
      amount: campaignAmount ?? marketPrice.amount,
      ...(campaignAmount === undefined ? {} : {originalAmount: marketPrice.amount, campaignAmount}),
      currency: marketPrice.currency || (normalizedMarketKey === 'NO' ? 'NOK' : undefined),
      vatIncluded: marketPrice.vatIncluded,
    }
  }

  return null
}

export const productPricesForMarket = (
  product: ProductDocument,
  marketKey?: string,
  now: Date = new Date(),
) =>
  (product.variants || [])
    .map((variant) => productVariantPriceForMarket(variant, marketKey, now))
    .filter((price): price is NonNullable<ReturnType<typeof productVariantPriceForMarket>> =>
      Boolean(price),
    )

export const productFromPriceForMarket = (
  product: ProductDocument,
  marketKey?: string,
  now: Date = new Date(),
) => {
  const prices = productPricesForMarket(product, marketKey, now)
  if (!prices.length) return null

  return prices.reduce((lowest, price) => (price.amount < lowest.amount ? price : lowest))
}

// Feed-PIM statuses that should appear in catalog/search listings AND be directly reachable.
// Per the client's ruling (Daniel Ljunggren Andersen, JO2 Slack 2026-07-03): ONLY `Published`
// products are listed on the site; every other status (Discontinued, EOL, …) is excluded from
// listings, and its detail page stays reachable-but-noindexed (see the noindex logic in the
// product-detail metadata). EOL was previously (incorrectly) listed here — the client explicitly
// grouped EOL with the statuses that "must not appear" for sale.
// This is an ALLOW-LIST: any status not in here (including future/unrecognized ones) is treated
// as not-listed by isProductListed/isProductDiscontinued and hidden from listings. If Feed-PIM
// introduces a new sellable status (e.g. "Available"), add it here or the products silently
// disappear from the catalog until it is. Keep values lowercase (comparisons normalize to lower).
export const LISTED_STATUSES = ['published']

// Feed-PIM statuses that are internal workflow states and must never render a page at all
// (excluded from listings, search, AND direct/QR-code access — everything else, including
// Discontinued, stays directly reachable even though it's excluded from listings).
export const HIDDEN_STATUSES = ['underenrichment', 'readytopublish', 'preeol', 'unpublished']

export const isProductListed = (product?: Pick<ProductDocument, 'status'> | null) => {
  const raw = product?.status
  if (raw === true) return true
  if (raw === false) return false

  const normalized = String(raw ?? '')
    .trim()
    .toLowerCase()
  // No status synced yet — preserve prior visibility rather than mass-hiding untracked products.
  if (!normalized) return true

  return LISTED_STATUSES.includes(normalized)
}

export const isProductStatusHidden = (product?: Pick<ProductDocument, 'status'> | null) => {
  const raw = product?.status
  if (typeof raw !== 'string') return false

  const normalized = raw.trim().toLowerCase()
  if (!normalized) return false

  return HIDDEN_STATUSES.includes(normalized)
}

// The complement of isProductListed. Named "discontinued" for historical/call-site reasons, but
// since the switch to an allow-list it is true for Discontinued AND the internal workflow statuses
// AND any unrecognized status — i.e. "should not appear in listings", not literally "discontinued".
// A null/empty (not-yet-synced) status is still considered listed, so this returns false for it.
export const isProductDiscontinued = (product?: Pick<ProductDocument, 'status'> | null) =>
  !isProductListed(product)

const PRODUCT_VARIANT_COLOR_FALLBACKS: Record<string, string> = {
  B: '#111111',
  BP: '#111111',
  BB: '#18171c',
  BL: '#111111',
  BK: '#111111',
  BLACK: '#111111',
  W: '#f5f1e8',
  WH: '#f5f1e8',
  WHITE: '#f5f1e8',
  SIL: '#b7bcc4',
  SILVER: '#b7bcc4',
  GR: '#747b84',
  GREY: '#747b84',
  GRAY: '#747b84',
}

export const productVariantSwatchColor = (colorHex?: string, colorCode?: string) => {
  const sanitizedHex = String(colorHex || '')
    .replace(/[^\x20-\x7E]/g, '')
    .trim()
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(sanitizedHex)) {
    return sanitizedHex
  }
  if (!colorCode) return '#f5f5f5'

  const normalized = colorCode.trim().toUpperCase()
  return PRODUCT_VARIANT_COLOR_FALLBACKS[normalized] || '#f5f5f5'
}

export const manualGroupTitle = (manuals: ProductManualDocument[]) =>
  manuals[0]?.productTitle || manuals[0]?.productNumber || 'Product documents'

const normalizeManualProductKey = (value?: string | null) =>
  String(value || '')
    .replace(/[^\x20-\x7E]/g, '')
    .trim()
    .toUpperCase()

export const groupManualsByProduct = (documents: ProductManualDocument[]) => {
  const groups = new Map<string, ProductManualDocument[]>()

  for (const document of documents) {
    const normalizedProductNumber = normalizeManualProductKey(document.productNumber)
    const normalizedProductRef = normalizeManualProductKey(document.product?._id)
    const fallbackTitle = String(document.productTitle || 'manual')
      .replace(/[^\x20-\x7E]/g, '')
      .trim()
    const fallbackBrandKey = normalizeManualProductKey(document.brandKey) || 'BRAND'
    const key =
      normalizedProductNumber || normalizedProductRef || `${fallbackTitle}-${fallbackBrandKey}`
    const existing = groups.get(key)
    if (existing) existing.push(document)
    else groups.set(key, [document])
  }

  return Array.from(groups.values()).sort((left, right) =>
    (left[0]?.productTitle || left[0]?.productNumber || '').localeCompare(
      right[0]?.productTitle || right[0]?.productNumber || '',
    ),
  )
}

export const resolveProductTypeVariant = ({
  productType,
  site,
  canonicalKey,
  locale,
}: {
  productType: SiteAwareProductTypeCategoryDocument
  site: SiteLite
  canonicalKey?: string
  locale: string
}): ProductTypeCategorySiteVariant | null => {
  const variants = productType.variants || []
  if (!variants.length) return null

  const scoreVariant = (variant: ProductTypeCategorySiteVariant) => {
    const siteMatch = Boolean(
      site.siteId && variant.sites?.some((candidate) => candidate._id === site.siteId),
    )
    const canonicalKeyMatch = Boolean(canonicalKey && variant.canonicalKeys?.includes(canonicalKey))
    const localeMatch = Boolean(locale && variant.locales?.includes(locale))

    if (siteMatch) return 100
    if (canonicalKeyMatch && localeMatch) return 80
    if (canonicalKeyMatch) return 60
    if (localeMatch) return 40
    return 0
  }

  return (
    variants
      .map((variant) => ({variant, score: scoreVariant(variant)}))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)[0]?.variant || null
  )
}
